import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { API, toUrlSlug } from '../utils/api';

const SearchAndFilter = ({ 
  searchQuery, 
  setSearchQuery, 
  onSearch
}) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions(null);
        setShowDropdown(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/wine-deals/autocomplete?query=${encodeURIComponent(searchQuery)}&limit=5`);
        if (response.data.success) {
          setSuggestions(response.data.data);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = (type, value) => {
    setShowDropdown(false);
    setSearchQuery('');

    switch(type) {
      case 'wine':
        navigate(`/${value.slug}`);
        break;
      case 'wine_type':
        navigate(`/type/${toUrlSlug(value)}`);
        break;
      case 'variety':
        navigate(`/variety/${toUrlSlug(value)}`);
        break;
      case 'country':
        navigate(`/country/${toUrlSlug(value)}`);
        break;
      case 'region':
        navigate(`/region/${toUrlSlug(value)}`);
        break;
      case 'appellation':
        navigate(`/appellation/${toUrlSlug(value)}`);
        break;
      case 'combination':
        navigate(`/${value.url_path}`);
        break;
      default:
        break;
    }
  };

  const hasSuggestions = suggestions && (
    suggestions.wines.length > 0 ||
    suggestions.wine_types.length > 0 ||
    suggestions.varieties.length > 0 ||
    suggestions.countries.length > 0 ||
    suggestions.regions.length > 0 ||
    suggestions.appellations.length > 0 ||
    (suggestions.combinations && suggestions.combinations.length > 0)
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Input with Autocomplete */}
        <div className="flex-1 relative" ref={dropdownRef}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
          <Input
            placeholder="Search wines, varieties, countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-base"
            style={{borderColor: '#A98D8C'}}
            onFocus={(e) => {
              e.target.style.borderColor = '#5D0F0F';
              e.target.style.boxShadow = '0 0 0 1px #5D0F0F';
              if (searchQuery.length >= 2 && hasSuggestions) {
                setShowDropdown(true);
              }
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#A98D8C';
              e.target.style.boxShadow = 'none';
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setShowDropdown(false);
                onSearch();
              }
            }}
          />

          {/* Autocomplete Dropdown */}
          {showDropdown && hasSuggestions && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              {/* Wine Names */}
              {suggestions.wines.length > 0 && (
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">WINES</div>
                  {suggestions.wines.map((wine, index) => (
                    <div
                      key={`wine-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('wine', wine)}
                    >
                      🍷 {wine.name}
                    </div>
                  ))}
                </div>
              )}

              {/* Wine Types */}
              {suggestions.wine_types.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">WINE TYPES</div>
                  {suggestions.wine_types.map((type, index) => (
                    <div
                      key={`type-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('wine_type', type)}
                    >
                      🍇 {type}
                    </div>
                  ))}
                </div>
              )}

              {/* Varieties */}
              {suggestions.varieties.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">VARIETIES</div>
                  {suggestions.varieties.map((variety, index) => (
                    <div
                      key={`variety-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('variety', variety)}
                    >
                      🌿 {variety}
                    </div>
                  ))}
                </div>
              )}

              {/* Countries */}
              {suggestions.countries.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">COUNTRIES</div>
                  {suggestions.countries.map((country, index) => (
                    <div
                      key={`country-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('country', country)}
                    >
                      🌍 {country}
                    </div>
                  ))}
                </div>
              )}

              {/* Regions */}
              {suggestions.regions.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">REGIONS</div>
                  {suggestions.regions.map((region, index) => (
                    <div
                      key={`region-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('region', region)}
                    >
                      📍 {region}
                    </div>
                  ))}
                </div>
              )}

              {/* Appellations */}
              {suggestions.appellations.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">APPELLATIONS</div>
                  {suggestions.appellations.map((appellation, index) => (
                    <div
                      key={`appellation-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('appellation', appellation)}
                    >
                      🏷️ {appellation}
                    </div>
                  ))}
                </div>
              )}

              {/* Combinations */}
              {suggestions.combinations && suggestions.combinations.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">POPULAR PAGES</div>
                  {suggestions.combinations.map((combo, index) => (
                    <div
                      key={`combo-${index}`}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm"
                      onClick={() => handleSuggestionClick('combination', combo)}
                    >
                      {combo.display_name} <span className="text-gray-400 text-xs">({combo.count} deals)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Search Button */}
        <Button 
          onClick={() => {
            setShowDropdown(false);
            onSearch();
          }}
          style={{backgroundColor: '#5D0F0F'}}
          className="h-12 px-8 hover:opacity-90 text-white"
        >
          <Search className="w-5 h-5 mr-2" />
          Search
        </Button>
      </div>
    </div>
  );
};

export default SearchAndFilter;
